/**
 * Fog of war. A per-tile `explored` bitmap starts fully unexplored; `reveal()`
 * uncovers a radius (driven by the camera focus and, later, by unit vision).
 * Rendering mirrors the terrain chunking: unexplored tiles are painted as dark
 * iso diamonds baked per chunk, re-baked only when a chunk's exploration changes.
 */
import { Container, Graphics, Sprite, RenderTexture } from 'pixi.js';
import type { Renderer } from 'pixi.js';
import { gridToWorld, worldToGrid, CHUNK_SIZE } from '@settlers/shared';
import type { GameMap } from '@settlers/engine';
import { chunkWorldBounds, chunkCountX, chunkCountY, diamondPoints } from '../render/geometry.js';
import type { WorldBox } from '../render/geometry.js';

interface FogChunk {
  readonly cx: number;
  readonly cy: number;
  readonly bounds: WorldBox;
  readonly sprite: Sprite;
  readonly texture: RenderTexture;
  dirty: boolean;
  fullyExplored: boolean;
}

const FOG_COLOR = 0x05070c;

export class FogOfWar {
  readonly stage = new Container();
  private readonly explored: Uint8Array;
  private readonly chunks: FogChunk[] = [];
  private readonly cols: number;
  private readonly rows: number;

  constructor(
    private readonly renderer: Renderer,
    private readonly map: GameMap,
  ) {
    this.explored = new Uint8Array(map.tileCount);
    this.stage.sortableChildren = true;
    this.cols = chunkCountX(map);
    this.rows = chunkCountY(map);

    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        const bounds = chunkWorldBounds(this.map, cx, cy);
        const w = Math.max(1, Math.ceil(bounds.maxX - bounds.minX));
        const h = Math.max(1, Math.ceil(bounds.maxY - bounds.minY));
        const texture = RenderTexture.create({ width: w, height: h, resolution: 1 });
        const sprite = new Sprite(texture);
        sprite.position.set(bounds.minX, bounds.minY);
        sprite.zIndex = cx + cy;
        this.stage.addChild(sprite);
        this.chunks.push({ cx, cy, bounds, sprite, texture, dirty: true, fullyExplored: false });
      }
    }
    this.bakeDirty();
  }

  /** Reveal a circular area (radius in tiles) around a world-space point. */
  reveal(worldX: number, worldY: number, radiusTiles: number): void {
    const g = worldToGrid(worldX, worldY);
    const gx = Math.round(g.x);
    const gy = Math.round(g.y);
    const r = Math.ceil(radiusTiles);
    const r2 = radiusTiles * radiusTiles;
    for (let y = gy - r; y <= gy + r; y++) {
      for (let x = gx - r; x <= gx + r; x++) {
        if (!this.map.inBounds(x, y)) continue;
        const dx = x - gx;
        const dy = y - gy;
        if (dx * dx + dy * dy > r2) continue;
        const i = y * this.map.width + x;
        if (this.explored[i] === 0) {
          this.explored[i] = 1;
          this.markChunkDirty(x, y);
        }
      }
    }
  }

  private markChunkDirty(tileX: number, tileY: number): void {
    const cx = Math.floor(tileX / CHUNK_SIZE);
    const cy = Math.floor(tileY / CHUNK_SIZE);
    const chunk = this.chunks[cy * this.cols + cx];
    if (chunk) chunk.dirty = true;
  }

  /** Re-bake chunks whose exploration changed. Call once per frame. */
  bakeDirty(): void {
    for (const chunk of this.chunks) {
      if (!chunk.dirty) continue;
      this.bake(chunk);
    }
  }

  private bake(chunk: FogChunk): void {
    const g = new Graphics();
    const x0 = chunk.cx * CHUNK_SIZE;
    const y0 = chunk.cy * CHUNK_SIZE;
    const x1 = Math.min(x0 + CHUNK_SIZE, this.map.width);
    const y1 = Math.min(y0 + CHUNK_SIZE, this.map.height);

    let anyHidden = false;
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        if (this.explored[y * this.map.width + x] === 1) continue;
        anyHidden = true;
        const c = gridToWorld(x, y, this.map.elevationAt(x, y));
        g.poly(diamondPoints(c.x - chunk.bounds.minX, c.y - chunk.bounds.minY)).fill(FOG_COLOR);
      }
    }

    this.renderer.render({ container: g, target: chunk.texture, clear: true });
    g.destroy();
    chunk.dirty = false;
    chunk.fullyExplored = !anyHidden;
    chunk.sprite.visible = !chunk.fullyExplored;
  }

  destroy(): void {
    for (const chunk of this.chunks) {
      chunk.texture.destroy(true);
      chunk.sprite.destroy();
    }
    this.chunks.length = 0;
    this.stage.destroy({ children: true });
  }
}
